var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useRef, useState, useEffect, useMemo } from 'react';
import { DataContext } from '../player/DataContext';
import globalStore from '../store';
import { GRID_SIZE, TICK_HEIGHT } from '../constants/numberLineConfig';
import { findParentLineGeometry, computePxPerUnit } from '../helper/numberLineHelper';
import { saveState } from '../helper/stateManagerHelper';
import { bringManipulativesToFront } from '../helper/helper';
import MathEditor from '../components/MathEditor';
import ButtonComponent from '../components/ButtonComponent';
import '../styles/widgets/numberLineCustomTick.scss';
import { t } from 'i18next';
import customEventHandler from '../helper/customEvents';
import { useDuplicateAnnounce, useCreationAnnounce } from '../helper/hooks/useDuplicateAnnounce';
import useAnnouncer from '../hooks/useAnnouncer';
var NumberLineCustomTick = function (_a) {
    var _b, _c;
    var data = _a.data;
    var _d = useContext(DataContext), manipulative = _d.manipulative, setManipulative = _d.setManipulative, selectedTool = _d.selectedTool, setSelectedTool = _d.setSelectedTool, setContextMenu = _d.setContextMenu;
    var zoomLevel = globalStore.useStoreState(function (store) { return store.playerStore; }).zoomLevel;
    var _e = globalStore.useStoreState(function (s) { return s.jsonDataStore; }), bottomToolConfig = _e.bottomToolConfig, btnConfig = _e.btnConfig;
    var id = data.id, numberLineId = data.numberLineId, center = data.center, position = data.position, label = data.label, isAbove = data.isAbove, scale = data.scale, lock = data.lock;
    // True when the tick itself is selected OR when the MathEditor inside it
    // is focused (its selectionType is `nl_${id}_label_0`, which contains id).
    var isFocused = selectedTool.some(function (s) { return s === id || s.startsWith("nl_".concat(id, "_")); });
    // Look up parent number line
    var parentLine = useMemo(function () {
        if (!numberLineId)
            return null;
        var parent = manipulative.find(function (m) { return m.data.id === numberLineId; });
        if (!parent || parent.data.type !== 'numberLine')
            return null;
        return parent.data;
    }, [numberLineId, manipulative]);
    var parentScale = (parentLine === null || parentLine === void 0 ? void 0 : parentLine.scale) || 1;
    var pxPerUnit = parentLine
        ? computePxPerUnit(parentLine.gridSquaresPerUnit, parentLine.intervalNum, !!parentLine.scalingToManipulatives)
        : GRID_SIZE;
    // Compute world position from parent line geometry
    // Position is in world space (already scaled) — no additional CSS scale needed
    var worldPosition = useMemo(function () {
        if (!parentLine)
            return { x: center.x, y: center.y };
        var geo = findParentLineGeometry(parentLine);
        var ctPx = (position - parentLine.startNum) * geo.pxPerUnit;
        return {
            x: geo.lineStartX + ctPx,
            y: geo.lineWorldY,
        };
    }, [parentLine, position, center]);
    // Ref for stale closure prevention
    var manipulativeRef = useRef(manipulative);
    useEffect(function () {
        manipulativeRef.current = manipulative;
    }, [manipulative]);
    // Transient CSS-only bring-to-front boost while this tick (or its parent
    // line, dragging the whole family) is actively being dragged.
    var _f = useState(false), isDragging = _f[0], setIsDragging = _f[1];
    useEffect(function () {
        var onDragActive = function (ids) { return setIsDragging(ids.includes(id)); };
        customEventHandler.addEventListener('numberLineDragActive', onDragActive);
        return function () {
            customEventHandler.removeEventListener('numberLineDragActive', onDragActive);
        };
    }, [id]);
    var dataRef = useRef(data);
    useEffect(function () {
        dataRef.current = data;
    }, [data]);
    // Pre-edit snapshot for the label editor's undo/redo. `dataRef.current`
    // itself gets continuously overwritten (via the effect above) as the user
    // types, so it can't be used at commit time as the "old" value — same bug
    // class as the drag-undo fix. Captured once whenever editing starts (see
    // the `isEditing` effect below), read at commit in `handleClickOutside`.
    var editStartDataRef = useRef(null);
    var announce = useAnnouncer().announce;
    // --- A11Y: creation / duplicate label overrides ---
    var tickName = t('text.customTickRoleDescription');
    var duplicateLabel = useDuplicateAnnounce(id, tickName);
    var creationLabel = useCreationAnnounce(id, tickName);
    // Outer wrapper ref — used to position the context menu when opened via
    // keyboard (Enter / Space / Shift+F10 / ContextMenu key).
    var outerRef = useRef(null);
    // Pretty-print a position for screen readers + announcements.
    // Integer NLs (parentLine.numberType === 0) drop fractional rendering;
    // others keep up to 2 decimals and strip trailing zeros.
    var formatPosition = useCallback(function (p) {
        var isIntegerNL = (parentLine === null || parentLine === void 0 ? void 0 : parentLine.numberType) === 0;
        if (isIntegerNL)
            return String(Math.round(p));
        var fixed = p.toFixed(2);
        return fixed.replace(/\.?0+$/, '') || '0';
    }, [parentLine === null || parentLine === void 0 ? void 0 : parentLine.numberType]);
    // Flatten the math-editor label array to plain text for the aria-label.
    // Math segments fall back to their value, so "\frac{1}{2}" becomes "1/2"
    // — adequate for SR announcement; MathLive's own a11y handles in-editor
    // narration when the user enters edit mode.
    var labelText = useMemo(function () {
        if (!Array.isArray(label))
            return '';
        return label
            .map(function (seg) {
            var _a, _b, _c;
            if ((seg === null || seg === void 0 ? void 0 : seg.type) === 'text')
                return String((_a = seg.value) !== null && _a !== void 0 ? _a : '');
            return String((_c = (_b = seg === null || seg === void 0 ? void 0 : seg.latex) !== null && _b !== void 0 ? _b : seg === null || seg === void 0 ? void 0 : seg.value) !== null && _c !== void 0 ? _c : '');
        })
            .join('')
            .trim();
    }, [label]);
    // Composed aria-label for the outer wrapper. Includes role-description-style
    // hint so the SR user knows what keys do, but only when not editing
    // (during edit, MathEditor owns the announcements).
    var computedAriaLabel = useMemo(function () {
        var base = t('text.customTickAriaLabel', {
            position: formatPosition(position),
            labelPart: labelText ? ", ".concat(labelText) : '',
        });
        return "".concat(base, ". ").concat(t('text.customTickHint'));
    }, [position, labelText, formatPosition]);
    // --- DRAG ---
    var onMouseDown = useCallback(function (e) {
        // The math keypad is portaled to document.body, but React synthetic
        // events still bubble through the REACT tree back to this wrapper's
        // onMouseDown / onTouchStart. A keypad button press would otherwise
        // start a tick "drag" and reset selectedTool to [id] — dropping the
        // label editor's selectionType (`nl_<id>_label_0`), flipping the
        // MathEditor's isFocused false and closing the virtual keypad between
        // every keystroke. Ignore events whose DOM target isn't physically
        // inside this tick (i.e. a portaled keypad descendant in <body>).
        var outside = e.currentTarget instanceof HTMLElement && !e.currentTarget.contains(e.target);
        if (outside) {
            return;
        }
        // While the label editor is active, a press on any child (the editorDiv,
        // the math-text button, the portaled keypad bubbling through the React
        // tree) must NOT re-run the tick drag/select. setSelectedTool([id]) here
        // drops the label editor's selectionType (`nl_<id>_label_0`), flips the
        // MathEditor's isFocused false and closes the editor+keypad the instant
        // it opens. Use the ref (not the captured `isEditing`, which is stale in
        // this useCallback). A genuine drag starts from the non-editing state.
        if (isEditingRef.current)
            return;
        if (lock === null || lock === void 0 ? void 0 : lock.positioning)
            return;
        e.stopPropagation();
        setSelectedTool([id]);
        customEventHandler.dispatchEvent('numberLineDragActive', [id]);
        var startClientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
        var startClientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
        var origPosition = dataRef.current.position;
        var origIsAbove = dataRef.current.isAbove;
        // Stable pre-drag snapshot — dataRef.current itself gets continuously
        // overwritten (via the effect above) as `data` updates during the drag,
        // so it can't be reused at drag-end for the undo/redo "old" value.
        var dragStartData = __assign({}, dataRef.current);
        var onMove = function (ev) {
            var clientX = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
            var clientY = 'touches' in ev ? ev.touches[0].clientY : ev.clientY;
            var zoomScale = zoomLevel * parentScale;
            var deltaX = (clientX - startClientX) / zoomScale;
            var deltaY = (clientY - startClientY) / zoomScale;
            var deltaUnits = deltaX / pxPerUnit;
            var newPosition = origPosition + deltaUnits;
            if (parentLine) {
                newPosition = Math.max(parentLine.startNum, Math.min(parentLine.endNum, newPosition));
            }
            // Vertical flip: if dragged past threshold, toggle above/below
            var flipThreshold = TICK_HEIGHT;
            var newIsAbove = origIsAbove;
            if (!origIsAbove && deltaY < -flipThreshold)
                newIsAbove = true;
            if (origIsAbove && deltaY > flipThreshold)
                newIsAbove = false;
            setManipulative(function (prev) {
                return prev.map(function (item) {
                    if (item.data.id === id) {
                        return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { position: parseFloat(newPosition.toFixed(6)), isAbove: newIsAbove }) });
                    }
                    return item;
                });
            });
        };
        var onUp = function () {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
            var currentItem = manipulativeRef.current.find(function (m) { return m.data.id === id; });
            // Guard against a phantom no-op save: touch devices fire a
            // synthetic "ghost click" (compatibility mousedown/mouseup) shortly
            // after a real touch drag ends, re-entering this handler with zero
            // movement. Without this check that ghost click pushes a second,
            // identical (old === new) undo/redo entry for every real drag.
            var noOpMove = currentItem && JSON.stringify(dragStartData) === JSON.stringify(currentItem.data);
            if (currentItem && !noOpMove) {
                var beforeIds = manipulativeRef.current.map(function (m) { var _a; return (_a = m.data.id) !== null && _a !== void 0 ? _a : ''; });
                var alreadyAtFront = beforeIds[beforeIds.length - 1] === id;
                var batchEntries = [
                    {
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'completeData',
                        old: JSON.stringify(dragStartData),
                        new: JSON.stringify(currentItem.data),
                    },
                ];
                if (!alreadyAtFront) {
                    // Functional update against live `prev`, not a replacement built
                    // from manipulativeRef.current — defensive against any pending
                    // position update from the drag not yet reflected in the ref.
                    batchEntries.push({
                        type: id,
                        id: id.split('_')[1],
                        propertyName: 'reorder',
                        old: JSON.stringify(beforeIds),
                        new: JSON.stringify(__spreadArray(__spreadArray([], beforeIds.filter(function (bid) { return bid !== id; }), true), [id], false)),
                    });
                    setManipulative(function (prev) { return bringManipulativesToFront(prev, [id]); });
                }
                saveState(batchEntries);
                announce(t('text.customTickMoved', { position: formatPosition(currentItem.data.position) }));
            }
            customEventHandler.dispatchEvent('numberLineDragActive', []);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
    }, [id, lock, zoomLevel, parentScale, pxPerUnit, parentLine, setManipulative, announce, formatPosition]);
    // --- SELECTION ---
    var onClick = useCallback(function (e) {
        e.stopPropagation();
        setSelectedTool(function (prev) {
            if (e.metaKey || e.ctrlKey || e.shiftKey) {
                return prev.includes(id) ? prev.filter(function (s) { return s !== id; }) : __spreadArray(__spreadArray([], prev, true), [id], false);
            }
            return [id];
        });
    }, [id, setSelectedTool]);
    // --- EDITABLE LABEL ---
    var _g = useState(false), isEditing = _g[0], setIsEditing = _g[1];
    // Live mirror of isEditing for event handlers that close over a stale value
    // (onMouseDown is a useCallback whose deps intentionally exclude isEditing).
    var isEditingRef = useRef(false);
    useEffect(function () {
        isEditingRef.current = isEditing;
    }, [isEditing]);
    // Snapshot the pre-edit data the moment editing begins (before any
    // keystroke can mutate it), so the commit handler has a stable "old" value.
    useEffect(function () {
        if (isEditing) {
            editStartDataRef.current = __assign({}, dataRef.current);
        }
    }, [isEditing]);
    var _h = useState(-1), mathKeyboardActive = _h[0], setMathKeyboardActive = _h[1];
    var mathEditorRef = useRef(null);
    var addMathText = useCallback(function () {
        setTimeout(function () {
            var _a;
            // See NumberLineJumpBar.addMathText — focusKeypadOnOpen traps
            // keyboard / SR focus in the keypad until Escape returns to the
            // math button.
            (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.insertMath({ focusKeypadOnOpen: true });
        }, 10);
    }, []);
    var handleLabelChange = useCallback(function (val) {
        setManipulative(function (prev) {
            return prev.map(function (item) {
                if (item.data.id === id) {
                    return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { label: val }) });
                }
                return item;
            });
        });
    }, [id, setManipulative]);
    // Enter editing when keyboard Tab lands directly on the label text
    // field (skipping the wrapper's Enter handler). MathEditor's onFocus
    // calls focusHandler → setSelectedTool([selectionType]); detecting
    // that selectionType is in selectedTool tells us the editorDiv just
    // gained focus, so we mirror isEditing accordingly. Surfaces the
    // math editor button so Tab can reach it.
    var labelSelectionType = "nl_".concat(id, "_label_0");
    useEffect(function () {
        if (!selectedTool.includes(labelSelectionType))
            return;
        if (lock === null || lock === void 0 ? void 0 : lock.positioning)
            return;
        if (isEditing)
            return;
        setIsEditing(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTool, labelSelectionType, lock === null || lock === void 0 ? void 0 : lock.positioning]);
    // Click-outside to exit editing (scoped to this specific tick's label box)
    var tickBoxRef = useRef(null);
    useEffect(function () {
        if (!isEditing)
            return;
        var handleClickOutside = function (e) {
            var _a, _b;
            var target = e.target;
            if (((_a = tickBoxRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)) ||
                target.closest('.ML__keyboard') ||
                target.closest('[part="keyboard"]') ||
                target.closest('.keypadWrapper')) {
                return;
            }
            var currentItem = manipulativeRef.current.find(function (m) { return m.data.id === id; });
            if (currentItem) {
                saveState({
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'completeData',
                    old: JSON.stringify((_b = editStartDataRef.current) !== null && _b !== void 0 ? _b : dataRef.current),
                    new: JSON.stringify(currentItem.data),
                });
                editStartDataRef.current = null;
            }
            setIsEditing(false);
        };
        window.addEventListener('mousedown', handleClickOutside, true);
        window.addEventListener('touchstart', handleClickOutside, true);
        return function () {
            window.removeEventListener('mousedown', handleClickOutside, true);
            window.removeEventListener('touchstart', handleClickOutside, true);
        };
    }, [isEditing, id]);
    // --- Bottom panel listener for delete ---
    useEffect(function () {
        if (!isFocused)
            return;
        var onToolClick = function (e) {
            var _a;
            if (((_a = e === null || e === void 0 ? void 0 : e.detail) === null || _a === void 0 ? void 0 : _a.id) === 'edit') {
                setIsEditing(true);
            }
        };
        customEventHandler.addEventListener('bottomPanel', onToolClick);
        return function () { return customEventHandler.removeEventListener('bottomPanel', onToolClick); };
    }, [isFocused]);
    // --- A11Y: keyboard handler on the outer wrapper ---
    // The widget is keyboard-focusable (tabIndex=0). On Enter / Space / Shift+F10
    // / ContextMenu key we open the existing right-click context menu so the
    // user can pick Move / Delete from there — matches DiceManipulative.
    // Per-axis arrow-key movement is intentionally NOT handled here; movement
    // happens only via the context menu's Move buttons.
    var onKeyDown = useCallback(function (e) {
        var _a, _b;
        // Hands-off while the label editor is active so typing isn't hijacked.
        if (isEditing) {
            if (e.key === 'Escape') {
                e.stopPropagation();
                e.preventDefault();
                setIsEditing(false);
                (_b = (_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.blur) === null || _b === void 0 ? void 0 : _b.call(_a);
            }
            return;
        }
        // Wrapper-level Enter / Space enters editing mode for the label.
        // Strict target check so the same key in the inner text-box stays
        // owned by the textbox (typing) and doesn't re-trigger the
        // wrapper. The kebab "Open options" button (Tab past the label
        // and math button) handles the context menu; right-click and
        // Shift+F10 still work through WhiteBoard's contextmenu handler.
        if (e.target !== e.currentTarget)
            return;
        if (e.key !== 'Enter' && e.key !== ' ')
            return;
        if (lock === null || lock === void 0 ? void 0 : lock.positioning)
            return;
        e.preventDefault();
        e.stopPropagation();
        setIsEditing(true);
        requestAnimationFrame(function () { var _a; return (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus(); });
    }, [isEditing, lock === null || lock === void 0 ? void 0 : lock.positioning]);
    if (!parentLine)
        return null;
    return (_jsx("div", { ref: outerRef, 
        // Treat in-edit as a selected state so the halo persists while the
        // user types. MathEditor's focus handler calls setSelectedTool with
        // its own selection key (`nl_<id>_label_0`), which replaces the
        // tick's id and otherwise drops `.selected` from the wrapper —
        // visually the tick would lose its glow the moment the cursor
        // entered the editor.
        className: "numberLineCustomTickManipulative ".concat(isFocused || isEditing ? 'selected' : ''), "data-manipulative-id": id, style: {
            position: 'absolute',
            left: worldPosition.x,
            top: worldPosition.y,
            // CSS-only bring-to-front while actively dragging — permanent
            // stacking comes from array position (reordered on drop).
            zIndex: isDragging ? 9999 : undefined,
        }, onClick: onClick, 
        // --- WCAG 2.2 a11y ---
        // role="group" (NOT "button") because the tick contains an
        // interactive label text-box (the MathEditor) as a descendant.
        // ARIA forbids interactive descendants of buttons, so screen
        // readers collapse the subtree to just the button's name and the
        // inner editable text becomes unreachable. Group permits children
        // to remain navigable while aria-label still names the whole
        // tick. Enter / Space / Shift+F10 / ContextMenu key still opens
        // the right-click menu via the onKeyDown handler below.
        role: "group", "aria-roledescription": tickName, "aria-label": (_b = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _b !== void 0 ? _b : computedAriaLabel, "aria-disabled": (lock === null || lock === void 0 ? void 0 : lock.positioning) ? true : undefined, 
        // Skip the wrapper while the MathEditor inside is active so Shift+Tab
        // doesn't bounce focus between the two on every keystroke.
        tabIndex: isEditing ? -1 : 0, onFocus: function (e) {
            if (e.target !== e.currentTarget)
                return;
            if (!selectedTool.includes(id))
                setSelectedTool([id]);
        }, onKeyDown: onKeyDown, children: _jsxs("div", { className: "numberLineCustomTickWrapper".concat(isAbove ? ' above' : ''), style: { left: 0, top: 0 }, onMouseDown: onMouseDown, onTouchStart: onMouseDown, children: [_jsx("div", { className: "numberLineCustomTick", "aria-hidden": "true", style: {
                        top: (-TICK_HEIGHT / 2) * parentScale,
                        height: TICK_HEIGHT * parentScale,
                    } }), _jsxs("div", { ref: tickBoxRef, className: "customTickBox customTickBoxEditing".concat(isEditing ? ' customTickBox--editing' : ''), style: {
                        top: isAbove
                            ? (-TICK_HEIGHT / 2) * parentScale - 35 - (GRID_SIZE / 2) * parentScale
                            : (TICK_HEIGHT / 2) * parentScale + 38 * parentScale - (GRID_SIZE / 2) * parentScale,
                        transform: "translateX(-50%) scale(".concat(parentScale, ")"),
                        transformOrigin: isAbove ? 'bottom center' : 'top center',
                    }, onTouchEnd: function (e) {
                        // Single-tap enters edit mode. Defer focus to the next frame so
                        // the editing class has committed before the MathEditor is
                        // focused — without that, the pointer-events:none rule that
                        // gates non-editing taps still applies and focus never lands.
                        //
                        // Deliberately do NOT call e.preventDefault() here. iOS Safari
                        // synthesizes mousemove + mousedown + mouseup + click events
                        // ~300 ms after touchend, and child elements depend on those
                        // (e.g. the math button at .jumpBarMathBtnBelow only has
                        // onMouseDown). preventDefault would suppress the entire
                        // synthesized-mouse sequence and the math button would never
                        // fire its addMathText → insertMath chain — so the keypad
                        // never opens on touch devices. The duplicate setIsEditing /
                        // focus path between onTouchEnd and onClick is intentional and
                        // idempotent via the !isEditing guard in onClick.
                        e.stopPropagation();
                        setIsEditing(true);
                        requestAnimationFrame(function () { var _a; return (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus(); });
                    }, onClick: function (e) {
                        // Don't bubble to the canvas (which would clear selection).
                        // Open the editor on a single click; the focus() call below
                        // pushes the editor's selectionType (`${id}_label_0`) into
                        // selectedTool, which already keeps THIS tick "selected" for
                        // the shadow + right panel. Avoid an extra setSelectedTool([id])
                        // here — that briefly drops the editor's selectionType, flipping
                        // MathEditor's isFocused false and tearing down the just-opened
                        // keypad before the focus chain can re-establish it.
                        //
                        // Guard with !isEditing so subsequent clicks bubbling up from
                        // children (e.g. the math button at jumpBarMathBtnBelow that
                        // schedules an insertMath via addMathText) don't trigger a
                        // redundant editor.focus() — that re-focus races the
                        // freshly-inserted math-field's own focus chain and the keypad
                        // never opens.
                        e.stopPropagation();
                        if (!isEditing) {
                            setIsEditing(true);
                            requestAnimationFrame(function () { var _a; return (_a = mathEditorRef.current) === null || _a === void 0 ? void 0 : _a.focus(); });
                        }
                    }, children: [_jsx(MathEditor, { data: {
                                type: 'data',
                                data: {
                                    text: label || [],
                                    color: '#293273',
                                    alignment: 'center',
                                    size: 20,
                                },
                            }, color: "#293273", className: "customTickMathEditor", placeHolder: " ", isTextUpdate: false, idPreffix: "nl_".concat(id, "_label"), isManipulative: true, disableTransform: true, keypadButtons: bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig.mathKeypadBottom, index: 0, setActiveMathText: setMathKeyboardActive, offsetKeypadByEditorHeight: true, cb: function (val) { return handleLabelChange(val); }, ref: function (el) {
                                mathEditorRef.current = el;
                            }, blockNewLine: true, 
                            // Keep the math keypad open and the math-field focused for
                            // ~500 ms after a math-field is inserted. The CustomTick's
                            // math-text button is a sibling of MathEditor's editorDiv,
                            // so the click moves focus off the math-field (and on iPad,
                            // iOS Safari oscillates focus between mf and editorDiv).
                            // Without this the keypad disappears and the user has to
                            // tap the label again to keep typing.
                            stickyKeypadAfterInsertOnTouch: true }), isEditing && (_jsx(ButtonComponent, { type: "imageType5", imgSrc: __assign({}, btnConfig.mathtext.imgSrc), disabled: ((_c = mathEditorRef.current) === null || _c === void 0 ? void 0 : _c.mathFieldInstancesCount()) === 2, defaultCSS: isAbove ? 'jumpBarMathBtnAbove' : 'jumpBarMathBtnBelow', clickActive: true, isActive: mathKeyboardActive === 0, controlActiveFromOutside: true, ariaLabel: t('text.mathText'), title: t('text.mathText'), onMouseDown: function (e) {
                                e.preventDefault();
                                addMathText();
                            }, 
                            // Keyboard Enter / Space activation: see JumpBar's math
                            // button — same reason. ButtonComponent's keydown handler
                            // dispatches a synthetic click on Enter / Space which
                            // bypasses onMouseDown, so we mirror the action here.
                            // insertMath()'s 300 ms guard prevents double-insert from
                            // mouse mousedown + click both firing.
                            clickHandler: function () { return addMathText(); }, showToolTip: true, direction: isAbove ? 'top' : 'bottom', isWhiteBoard: true, isParentRelative: true })), isFocused && (_jsx("button", { type: "button", className: "numberLineKebabBtn ".concat(isAbove ? 'numberLineKebabBtn--above' : 'numberLineKebabBtn--below'), "aria-label": t('text.openOptions') || 'Open options', "aria-haspopup": "menu", onMouseDown: function (e) { return e.stopPropagation(); }, onTouchStart: function (e) { return e.stopPropagation(); }, onClick: function (e) {
                                e.stopPropagation();
                                if (!selectedTool.includes(id))
                                    setSelectedTool([id]);
                                var rect = e.currentTarget.getBoundingClientRect();
                                setContextMenu({ x: rect.right, y: rect.top, manipulativeId: id });
                            }, children: _jsx("img", { src: "./assets/images/icons/kebabN.svg", alt: "", "aria-hidden": "true" }) }))] })] }) }));
};
export default NumberLineCustomTick;
